import { useCallback, useEffect, useRef, useState } from 'react';

import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import * as utils from './useNextRender';

/**
 * @method
 * Stores a callback that will be invoked on the next render cycle
 * @returns {[scheduledRender]} - Schduler function
 */
export const useNextRender = () => {
  const storedCallback = useRef( null );
  const [scheduled, setScheduled] = useState( 0 );

  const setScheduledFn = useCallback( utils.composeScheduledRender( { storedCallback }, { setScheduled } ), [
    setScheduled
  ] );

  useEffect( () => {
    if( scheduled === 0 ){
      return;
    }

    storedCallback.current();

    utils.cleanup( { storedCallback }, { setScheduled } );
  }, [scheduled] );

  useEffect( () => {
    return () => {
      utils.cleanup( { storedCallback }, { setScheduled } );
    };
  }, [] );

  return [setScheduledFn];
};

/**
 * Teardown
 * @param {object} data - arguments
 * @param {object} methods - methods
 */
export const cleanup = ( data, methods ) => {
  const { storedCallback = {} } = handleEmptyObjects( data );
  const { setScheduled } = methods || {};

  if( !setScheduled ){
    return;
  }

  storedCallback.current = null;
  setScheduled( 0 );
};

/**
 * @callback scheduledRender
 * @param {function} callback - Method to invoke on next render
 */

/**
 * Manages storing a function to be called during the next render cycle
 * @param {object} data - arguments
 * @param {function} data.storedCallback - stored callback
 * @param {object} methods - methods
 * @param {function} methods.setScheduled - set scheduled function
 * @returns {scheduledRender} - composed function
 */
export const composeScheduledRender = ( data, methods ) => ( cb ) => {
  const { storedCallback = {} } = handleEmptyObjects( data );
  const { setScheduled } = methods || {};

  if( typeof cb !== 'function' || !setScheduled ){
    return;
  }

  storedCallback.current = cb;
  setScheduled( Date.now() );
};
